import { useMemo } from "react";
import { MiBadge } from "@miplatform/components";
import { useTheme } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { useCalendarContext } from "../../../providers";

function getAvailabilityColors(
  time: number,
  palette: Theme["palette"],
  capacityWarningThreshold: number,
  capacityCriticalThreshold: number
) {
  if (time <= capacityWarningThreshold && time > capacityCriticalThreshold) {
    return {
      background: palette.orange.light,
      foreground: palette.orange.darkAccent,
    };
  }
  if (time <= capacityCriticalThreshold) {
    return { background: palette.red.light, foreground: palette.red.dark };
  }
  return { background: palette.green.light, foreground: palette.green.dark };
}

function formatAvailabilityText(availableTime: number) {
  if (availableTime < 1) {
    const minutes = Math.round(availableTime * 60);
    return `${minutes}m Available`;
  }
  const hours = Math.floor(availableTime);
  const minutes = Math.round((availableTime - hours) * 60);
  if (minutes === 60) {
    return `${hours + 1}h Available`;
  }
  if (minutes === 0) {
    return `${hours}h Available`;
  }
  return `${hours}h${minutes}m Available`;
}

export const AvailabilityTimeBadge = ({
  availableTime,
  className,
}: AvailabilityTimeBadgeProps) => {
  const theme = useTheme();
  const { calendarSet } = useCalendarContext();
  const capacityWarningThreshold =
    calendarSet?.config?.capacityWarningThreshold || 3;
  const capacityCriticalThreshold =
    calendarSet?.config?.capacityCriticalThreshold || 1;
  const availability = useMemo(
    () =>
      getAvailabilityColors(
        availableTime,
        theme.palette,
        capacityWarningThreshold,
        capacityCriticalThreshold
      ),
    [
      availableTime,
      theme.palette,
      capacityWarningThreshold,
      capacityCriticalThreshold,
    ]
  );
  const availabilityText = useMemo(
    () => formatAvailabilityText(availableTime),
    [availableTime]
  );

  return (
    <MiBadge
      className={className}
      backgroundColor={availability.background}
      color={availability.foreground}
      borderColor={availability.foreground}
      text={availabilityText}
      fontSize={theme.fontSize.xsmall}
    />
  );
};

interface AvailabilityTimeBadgeProps {
  availableTime: number;
  className?: string;
}
